/**
 * 法律文书生成组件 - 全页面版本
 * 支持:
 * 1. DOCX 模板填充
 * 2. 法律文书 PDF 生成
 * 3. PDF 表单填充
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { CONTINENT_GROUPS, CountryInfo, ContinentGroup, searchCountries } from '../config/countriesData';

// API 基础 URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// 文档类型
type DocumentType = 'contract' | 'agreement' | 'notice' | 'letter';

// 签名信息
interface Signature {
    name: string;
    title?: string;
}

// 法律文书数据
interface LegalDocumentData {
    type: DocumentType;
    title: string;
    parties: string[];
    content: string;
    date: string;
    signatures: Signature[];
    country: string;
}

interface DocumentGeneratorProps {
    isOpen: boolean;
    onClose: () => void;
}

export const DocumentGenerator: React.FC<DocumentGeneratorProps> = ({ isOpen, onClose }) => {

    // 当前选择的功能
    const [activeTab, setActiveTab] = useState<'legal' | 'template' | 'form'>('legal');

    // 法律文书状态
    const [legalDoc, setLegalDoc] = useState<LegalDocumentData>({
        type: 'contract',
        title: '',
        parties: ['', ''],
        content: '',
        date: new Date().toISOString().split('T')[0],
        signatures: [{ name: '', title: '' }],
        country: 'CN',
    });

    // 国家选择器状态
    const [countrySearchQuery, setCountrySearchQuery] = useState('');
    const [expandedContinent, setExpandedContinent] = useState<string | null>('asia');
    const [isCountrySelectorOpen, setIsCountrySelectorOpen] = useState(false);
    const countrySelectorRef = useRef<HTMLDivElement>(null);

    // 模板状态
    const [templateFile, setTemplateFile] = useState<File | null>(null);
    const [templateVariables, setTemplateVariables] = useState<string[]>([]);
    const [templateData, setTemplateData] = useState<Record<string, string>>({});

    // PDF 表单状态
    const [pdfFile, setPdfFile] = useState<File | null>(null);
    const [pdfFields, setPdfFields] = useState<{ name: string; type: string; value?: string }[]>([]);
    const [pdfFormData, setPdfFormData] = useState<Record<string, string>>({});

    // 加载状态
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // 获取当前选中的国家信息
    const getSelectedCountry = (): CountryInfo | undefined => {
        for (const group of CONTINENT_GROUPS) {
            const found = group.countries.find(c => c.code === legalDoc.country);
            if (found) return found;
        }
        return undefined;
    };

    // 点击外部关闭国家选择器
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (countrySelectorRef.current && !countrySelectorRef.current.contains(event.target as Node)) {
                setIsCountrySelectorOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // 获取搜索结果
    const getFilteredCountries = (): ContinentGroup[] => {
        if (!countrySearchQuery) {
            return CONTINENT_GROUPS;
        }
        const results = searchCountries(countrySearchQuery);
        return CONTINENT_GROUPS.map(group => ({
            ...group,
            countries: group.countries.filter(c =>
                results.some(r => r.code === c.code)
            )
        })).filter(group => group.countries.length > 0);
    };

    // 生成法律文书 PDF
    const generateLegalDocument = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/documents/generate/legal`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(legalDoc),
            });

            if (!response.ok) {
                throw new Error('Failed to generate document');
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${legalDoc.type}-${Date.now()}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Generation failed');
        } finally {
            setIsLoading(false);
        }
    }, [legalDoc]);

    // 获取模板变量
    const getTemplateVariables = useCallback(async () => {
        if (!templateFile) return;

        setIsLoading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('template', templateFile);

            const response = await fetch(`${API_BASE_URL}/api/documents/template/variables`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();
            if (data.success && data.variables) {
                setTemplateVariables(data.variables);
                const initialData: Record<string, string> = {};
                data.variables.forEach((v: string) => { initialData[v] = ''; });
                setTemplateData(initialData);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to parse template');
        } finally {
            setIsLoading(false);
        }
    }, [templateFile]);

    // 从模板生成 DOCX
    const generateFromTemplate = useCallback(async () => {
        if (!templateFile) return;

        setIsLoading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('template', templateFile);
            formData.append('data', JSON.stringify(templateData));
            formData.append('outputName', `document-${Date.now()}.docx`);

            const response = await fetch(`${API_BASE_URL}/api/documents/generate/docx`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) throw new Error('Failed to generate document');

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `document-${Date.now()}.docx`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Generation failed');
        } finally {
            setIsLoading(false);
        }
    }, [templateFile, templateData]);

    // 获取 PDF 表单字段
    const getPdfFormFields = useCallback(async () => {
        if (!pdfFile) return;

        setIsLoading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('pdf', pdfFile);

            const response = await fetch(`${API_BASE_URL}/api/documents/form/fields`, {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();
            if (data.success && data.fields) {
                setPdfFields(data.fields);
                const initialData: Record<string, string> = {};
                data.fields.forEach((f: { name: string; value?: string }) => {
                    initialData[f.name] = f.value || '';
                });
                setPdfFormData(initialData);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to read PDF form');
        } finally {
            setIsLoading(false);
        }
    }, [pdfFile]);

    // 填充 PDF 表单
    const fillPdfForm = useCallback(async () => {
        if (!pdfFile) return;

        setIsLoading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('pdf', pdfFile);
            formData.append('formData', JSON.stringify(pdfFormData));
            formData.append('outputName', `filled-${Date.now()}.pdf`);

            const response = await fetch(`${API_BASE_URL}/api/documents/fill/pdf`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) throw new Error('Failed to fill PDF form');

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `filled-${Date.now()}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Fill failed');
        } finally {
            setIsLoading(false);
        }
    }, [pdfFile, pdfFormData]);

    const selectedCountryInfo = getSelectedCountry();

    if (!isOpen) return null;

    return (
        <div className="document-generator-page">
            {/* 顶部导航栏 */}
            <header className="page-header">
                <button className="back-btn" onClick={onClose}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                    <span>返回</span>
                </button>
                <h1>📄 法律文书生成</h1>
                <div className="header-spacer"></div>
            </header>

            {/* 标签切换 */}
            <div className="tab-container">
                <button
                    className={`tab-btn ${activeTab === 'legal' ? 'active' : ''}`}
                    onClick={() => setActiveTab('legal')}
                >
                    <span className="tab-icon">📋</span>
                    <span>法律文书</span>
                </button>
                <button
                    className={`tab-btn ${activeTab === 'template' ? 'active' : ''}`}
                    onClick={() => setActiveTab('template')}
                >
                    <span className="tab-icon">📝</span>
                    <span>模板填充</span>
                </button>
                <button
                    className={`tab-btn ${activeTab === 'form' ? 'active' : ''}`}
                    onClick={() => setActiveTab('form')}
                >
                    <span className="tab-icon">📑</span>
                    <span>表单填写</span>
                </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <main className="page-content">
                {/* 法律文书生成 */}
                {activeTab === 'legal' && (
                    <div className="tab-content-area fade-in">
                        <div className="form-card">
                            <h2>生成法律文书</h2>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>文书类型</label>
                                    <select
                                        value={legalDoc.type}
                                        onChange={(e) => setLegalDoc({ ...legalDoc, type: e.target.value as DocumentType })}
                                    >
                                        <option value="contract">合同</option>
                                        <option value="agreement">协议</option>
                                        <option value="notice">通知</option>
                                        <option value="letter">函件</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label>适用法域</label>
                                    <div className="country-selector-wrapper" ref={countrySelectorRef}>
                                        <button
                                            className="country-selector-trigger"
                                            onClick={() => setIsCountrySelectorOpen(!isCountrySelectorOpen)}
                                        >
                                            <span className="selected-country">
                                                <span className="flag">{selectedCountryInfo?.flag}</span>
                                                <span>{selectedCountryInfo?.name}</span>
                                            </span>
                                            <svg className={`chevron ${isCountrySelectorOpen ? 'open' : ''}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M6 9l6 6 6-6" />
                                            </svg>
                                        </button>

                                        {isCountrySelectorOpen && (
                                            <div className="country-dropdown">
                                                <div className="country-search">
                                                    <input
                                                        type="text"
                                                        placeholder="搜索国家/地区..."
                                                        value={countrySearchQuery}
                                                        onChange={(e) => setCountrySearchQuery(e.target.value)}
                                                        autoFocus
                                                    />
                                                </div>
                                                <div className="country-groups">
                                                    {getFilteredCountries().map((group) => (
                                                        <div key={group.id} className="country-group">
                                                            <button
                                                                className="group-header"
                                                                onClick={() => setExpandedContinent(
                                                                    expandedContinent === group.id ? null : group.id
                                                                )}
                                                            >
                                                                <span className="group-icon">{group.icon}</span>
                                                                <span className="group-name">{group.name}</span>
                                                                <span className="group-count">{group.countries.length}</span>
                                                                <svg className={`chevron ${expandedContinent === group.id ? 'open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                    <path d="M6 9l6 6 6-6" />
                                                                </svg>
                                                            </button>
                                                            {(expandedContinent === group.id || countrySearchQuery) && (
                                                                <div className="group-countries">
                                                                    {group.countries.map((c) => (
                                                                        <button
                                                                            key={c.code}
                                                                            className={`country-option ${legalDoc.country === c.code ? 'selected' : ''}`}
                                                                            onClick={() => {
                                                                                setLegalDoc({ ...legalDoc, country: c.code });
                                                                                setIsCountrySelectorOpen(false);
                                                                                setCountrySearchQuery('');
                                                                            }}
                                                                        >
                                                                            <span className="flag">{c.flag}</span>
                                                                            <span className="name">{c.name}</span>
                                                                            {legalDoc.country === c.code && (
                                                                                <svg className="check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                                    <path d="M20 6L9 17l-5-5" />
                                                                                </svg>
                                                                            )}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>标题</label>
                                <input
                                    type="text"
                                    value={legalDoc.title}
                                    onChange={(e) => setLegalDoc({ ...legalDoc, title: e.target.value })}
                                    placeholder="例如: 房屋租赁合同"
                                />
                            </div>

                            <div className="form-group">
                                <label>日期</label>
                                <input
                                    type="date"
                                    value={legalDoc.date}
                                    onChange={(e) => setLegalDoc({ ...legalDoc, date: e.target.value })}
                                />
                            </div>

                            <div className="form-group">
                                <label>当事人 (每行一个)</label>
                                <textarea
                                    value={legalDoc.parties.join('\n')}
                                    onChange={(e) => setLegalDoc({ ...legalDoc, parties: e.target.value.split('\n') })}
                                    placeholder="甲方: xxx&#10;乙方: xxx"
                                    rows={3}
                                />
                            </div>

                            <div className="form-group">
                                <label>正文内容</label>
                                <textarea
                                    value={legalDoc.content}
                                    onChange={(e) => setLegalDoc({ ...legalDoc, content: e.target.value })}
                                    placeholder="输入合同/协议正文..."
                                    rows={8}
                                />
                            </div>

                            <button
                                className="generate-btn"
                                onClick={generateLegalDocument}
                                disabled={isLoading || !legalDoc.title || !legalDoc.content}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="loading-spinner"></span>
                                        <span>生成中...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>📥</span>
                                        <span>生成 PDF</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* 模板填充 */}
                {activeTab === 'template' && (
                    <div className="tab-content-area fade-in">
                        <div className="form-card">
                            <h2>Word 模板填充</h2>

                            <div className="upload-area">
                                <input
                                    type="file"
                                    accept=".docx"
                                    id="template-upload"
                                    onChange={(e) => {
                                        setTemplateFile(e.target.files?.[0] || null);
                                        setTemplateVariables([]);
                                    }}
                                />
                                <label htmlFor="template-upload" className="upload-label">
                                    <span className="upload-icon">📄</span>
                                    <span className="upload-text">
                                        {templateFile ? templateFile.name : '点击选择 Word 模板 (.docx)'}
                                    </span>
                                </label>
                            </div>

                            {templateFile && (
                                <button
                                    className="secondary-btn"
                                    onClick={getTemplateVariables}
                                    disabled={isLoading}
                                >
                                    {isLoading ? '解析中...' : '解析模板变量'}
                                </button>
                            )}

                            {templateVariables.length > 0 && (
                                <div className="variables-section">
                                    <h3>模板变量 (使用 {'{{变量名}}'} 格式)</h3>
                                    {templateVariables.map((v) => (
                                        <div key={v} className="form-group">
                                            <label>{`{{${v}}}`}</label>
                                            <input
                                                type="text"
                                                value={templateData[v] || ''}
                                                onChange={(e) => setTemplateData({ ...templateData, [v]: e.target.value })}
                                            />
                                        </div>
                                    ))}

                                    <button
                                        className="generate-btn"
                                        onClick={generateFromTemplate}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? '生成中...' : '生成文档'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* PDF 表单填写 */}
                {activeTab === 'form' && (
                    <div className="tab-content-area fade-in">
                        <div className="form-card">
                            <h2>PDF 表单填写</h2>

                            <div className="upload-area">
                                <input
                                    type="file"
                                    accept=".pdf"
                                    id="pdf-upload"
                                    onChange={(e) => {
                                        setPdfFile(e.target.files?.[0] || null);
                                        setPdfFields([]);
                                    }}
                                />
                                <label htmlFor="pdf-upload" className="upload-label">
                                    <span className="upload-icon">📑</span>
                                    <span className="upload-text">
                                        {pdfFile ? pdfFile.name : '点击选择 PDF 表单'}
                                    </span>
                                </label>
                            </div>

                            {pdfFile && (
                                <button
                                    className="secondary-btn"
                                    onClick={getPdfFormFields}
                                    disabled={isLoading}
                                >
                                    {isLoading ? '读取中...' : '读取表单字段'}
                                </button>
                            )}

                            {pdfFields.length > 0 && (
                                <div className="fields-section">
                                    <h3>表单字段</h3>
                                    {pdfFields.map((f) => (
                                        <div key={f.name} className="form-group">
                                            <label>{f.name} <span className="field-type">({f.type})</span></label>
                                            {f.type === 'checkbox' ? (
                                                <label className="checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={pdfFormData[f.name] === 'true'}
                                                        onChange={(e) => setPdfFormData({
                                                            ...pdfFormData,
                                                            [f.name]: e.target.checked ? 'true' : 'false'
                                                        })}
                                                    />
                                                    <span>已勾选</span>
                                                </label>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={pdfFormData[f.name] || ''}
                                                    onChange={(e) => setPdfFormData({
                                                        ...pdfFormData,
                                                        [f.name]: e.target.value
                                                    })}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    <button
                                        className="generate-btn"
                                        onClick={fillPdfForm}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? '填充中...' : '填充并下载'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </main>

            <style>{`
                .document-generator-page {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: #ffffff;
                    display: flex;
                    flex-direction: column;
                    z-index: 1000;
                    overflow: hidden;
                }

                .page-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 32px;
                    border-bottom: 1px solid #e5e7eb;
                    background: #ffffff;
                }

                .back-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 8px 16px;
                    background: #f3f4f6;
                    border: none;
                    border-radius: 8px;
                    color: #374151;
                    font-size: 14px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .back-btn:hover {
                    background: #e5e7eb;
                    color: #111827;
                }

                .page-header h1 {
                    margin: 0;
                    font-size: 1.5rem;
                    color: #111827;
                    font-weight: 600;
                }

                .header-spacer {
                    width: 100px;
                }

                .tab-container {
                    display: flex;
                    gap: 8px;
                    padding: 16px 32px;
                    background: #fafafa;
                    border-bottom: 1px solid #e5e7eb;
                    justify-content: center;
                }

                .tab-btn {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px 24px;
                    background: #ffffff;
                    border: 2px solid #e5e7eb;
                    border-radius: 10px;
                    color: #6b7280;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .tab-btn:hover {
                    border-color: #d1d5db;
                    color: #374151;
                }

                .tab-btn.active {
                    background: #111827;
                    color: #ffffff;
                    border-color: #111827;
                }

                .tab-icon {
                    font-size: 18px;
                }

                .page-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 32px;
                }

                .tab-content-area {
                    max-width: 700px;
                    margin: 0 auto;
                }

                .fade-in {
                    animation: fadeIn 0.3s ease;
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                .form-card {
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 16px;
                    padding: 32px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.04);
                }

                .form-card h2 {
                    margin: 0 0 24px 0;
                    color: #111827;
                    font-size: 1.25rem;
                    font-weight: 600;
                }

                .form-row {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 16px;
                }

                .form-group {
                    margin-bottom: 20px;
                }

                .form-group label {
                    display: block;
                    color: #374151;
                    font-size: 14px;
                    font-weight: 500;
                    margin-bottom: 8px;
                }

                .form-group input[type="text"],
                .form-group input[type="date"],
                .form-group textarea,
                .form-group select {
                    width: 100%;
                    padding: 12px 14px;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    color: #111827;
                    font-size: 14px;
                    font-family: inherit;
                    transition: border-color 0.2s;
                }

                .form-group input:focus,
                .form-group textarea:focus,
                .form-group select:focus {
                    outline: none;
                    border-color: #111827;
                }

                textarea {
                    resize: vertical;
                }

                .field-type {
                    color: #9ca3af;
                    font-weight: normal;
                    font-size: 12px;
                }

                .checkbox-label {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                }

                .checkbox-label input {
                    width: 18px;
                    height: 18px;
                }

                /* Country Selector */
                .country-selector-wrapper {
                    position: relative;
                }

                .country-selector-trigger {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 12px 14px;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .country-selector-trigger:hover {
                    border-color: #d1d5db;
                }

                .selected-country {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }

                .selected-country .flag {
                    font-size: 20px;
                }

                .chevron {
                    transition: transform 0.2s;
                }

                .chevron.open {
                    transform: rotate(180deg);
                }

                .country-dropdown {
                    position: absolute;
                    top: calc(100% + 8px);
                    left: 0;
                    right: 0;
                    background: #ffffff;
                    border: 1px solid #e5e7eb;
                    border-radius: 12px;
                    box-shadow: 0 10px 40px rgba(0,0,0,0.12);
                    z-index: 100;
                    max-height: 350px;
                    overflow: hidden;
                    display: flex;
                    flex-direction: column;
                }

                .country-search {
                    padding: 12px;
                    border-bottom: 1px solid #e5e7eb;
                }

                .country-search input {
                    width: 100%;
                    padding: 10px 14px;
                    border: 1px solid #e5e7eb;
                    border-radius: 8px;
                    font-size: 14px;
                    outline: none;
                }

                .country-search input:focus {
                    border-color: #111827;
                }

                .country-groups {
                    overflow-y: auto;
                    flex: 1;
                }

                .country-group {
                    border-bottom: 1px solid #f3f4f6;
                }

                .group-header {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    padding: 12px 16px;
                    background: #fafafa;
                    border: none;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .group-header:hover {
                    background: #f3f4f6;
                }

                .group-icon {
                    font-size: 18px;
                }

                .group-name {
                    flex: 1;
                    text-align: left;
                    font-weight: 500;
                    color: #374151;
                }

                .group-count {
                    background: #e5e7eb;
                    color: #6b7280;
                    padding: 2px 8px;
                    border-radius: 10px;
                    font-size: 12px;
                }

                .group-countries {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 4px;
                    padding: 8px;
                    background: #ffffff;
                }

                .country-option {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 12px;
                    background: transparent;
                    border: none;
                    border-radius: 8px;
                    cursor: pointer;
                    transition: background 0.2s;
                    text-align: left;
                }

                .country-option:hover {
                    background: #f3f4f6;
                }

                .country-option.selected {
                    background: #f0fdf4;
                }

                .country-option .flag {
                    font-size: 20px;
                }

                .country-option .name {
                    flex: 1;
                    font-size: 13px;
                    color: #374151;
                }

                .country-option .check {
                    color: #10b981;
                }

                /* Upload Area */
                .upload-area {
                    margin-bottom: 20px;
                }

                .upload-area input[type="file"] {
                    display: none;
                }

                .upload-label {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                    padding: 32px;
                    border: 2px dashed #d1d5db;
                    border-radius: 12px;
                    cursor: pointer;
                    transition: all 0.2s;
                }

                .upload-label:hover {
                    border-color: #9ca3af;
                    background: #f9fafb;
                }

                .upload-icon {
                    font-size: 40px;
                }

                .upload-text {
                    color: #6b7280;
                    font-size: 14px;
                }

                /* Buttons */
                .generate-btn {
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    padding: 14px 24px;
                    background: #111827;
                    border: none;
                    border-radius: 10px;
                    color: #ffffff;
                    font-size: 15px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s;
                    margin-top: 8px;
                }

                .generate-btn:hover:not(:disabled) {
                    background: #1f2937;
                }

                .generate-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .secondary-btn {
                    width: 100%;
                    padding: 12px 20px;
                    background: #f3f4f6;
                    border: 1px solid #e5e7eb;
                    border-radius: 10px;
                    color: #374151;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    margin-bottom: 20px;
                    transition: all 0.2s;
                }

                .secondary-btn:hover:not(:disabled) {
                    background: #e5e7eb;
                }

                .secondary-btn:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }

                .loading-spinner {
                    width: 18px;
                    height: 18px;
                    border: 2px solid rgba(255,255,255,0.3);
                    border-top-color: #ffffff;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* Sections */
                .variables-section,
                .fields-section {
                    margin-top: 24px;
                    padding-top: 24px;
                    border-top: 1px solid #e5e7eb;
                }

                .variables-section h3,
                .fields-section h3 {
                    color: #111827;
                    font-size: 1rem;
                    font-weight: 600;
                    margin: 0 0 20px 0;
                }

                .error-message {
                    background: #fef2f2;
                    color: #dc2626;
                    padding: 12px 20px;
                    margin: 0 32px;
                    border-radius: 10px;
                    font-size: 14px;
                }

                /* Responsive */
                @media (max-width: 768px) {
                    .form-row {
                        grid-template-columns: 1fr;
                    }

                    .tab-container {
                        flex-wrap: wrap;
                    }

                    .group-countries {
                        grid-template-columns: 1fr;
                    }
                }
            `}</style>
        </div>
    );
};

export default DocumentGenerator;
